import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useFirstLoadGateStore } from '@revelio/core/first-load-gate';

import { getDataInspectorState } from './data-inspector.store';
import { createPageLoadTracker } from './page-load-timing';

/**
 * Longer than the first-load overlay's 150ms, for the same reason it has one
 * at all: a page hands off between loaders in stages. The widest gap seen is
 * a page re-running every query ~200ms after its first batch has drawn — a
 * window shorter than that reports the first batch as the whole load. The
 * figure still stops at the last zero, so the width costs nothing.
 */
const QUIET_PERIOD_MS = 500;

const pageLoadTracker = createPageLoadTracker({
  loaders: {
    getActiveLoaderCount: () =>
      useFirstLoadGateStore.getState().activeLoaderCount,
    subscribe: (listener) =>
      useFirstLoadGateStore.subscribe((state, previous) => {
        if (state.activeLoaderCount !== previous.activeLoaderCount) listener();
      }),
  },
  now: () => performance.now(),
  quietPeriodMs: QUIET_PERIOD_MS,
});

/**
 * Times each page visit for the Data Inspector: from the navigation until the
 * page's last loader has cleared.
 *
 * The start is stamped during render, not in the effect: this renders ahead of
 * the routes, and an effect would only fire after the new page's first render
 * pass — part of what is being measured. The store write waits for the layout
 * effect so no other component is updated mid-render; that is still before any
 * loader's passive effect.
 *
 * `pathname` only, matching how the records are grouped: a deeplink's query
 * string must not start a separate visit.
 *
 * Runs whether or not the viewer may see the inspector — the permission is
 * pushed in by an effect, later than the first page starts loading.
 */
export const DataInspectorNavigationSync = () => {
  const { pathname } = useLocation();
  const stampedPathname = useRef<string | null>(null);
  const stampedAt = useRef(0);

  if (stampedPathname.current !== pathname) {
    stampedPathname.current = pathname;
    stampedAt.current = performance.now();
  }

  useLayoutEffect(() => {
    const startedAt = stampedAt.current;
    getDataInspectorState().startPageLoad(pathname, startedAt);
    pageLoadTracker.start(startedAt, (durationMs) =>
      getDataInspectorState().finishPageLoad(pathname, durationMs)
    );
  }, [pathname]);

  return null;
};
